import React, { useMemo, useState, useEffect, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import { API_URL } from "../../constant";
import "./ManualTopCoinWindow.css";

const TOPCOIN_RULE_AMOUNT = 5;
const MTOPCOIN_PER_CURRENCY = 100;

const getDefaultDateTime = () => {
  const now = new Date();
  const tzOffsetMs = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - tzOffsetMs).toISOString().slice(0, 16);
};

export const calculateReward = (transactionTotal, distributionRule = null) => {
  const amount = Number(transactionTotal) || 0;
  if (amount <= 0) {
    return { topcoin: 0, mTopcoin: 0 };
  }

  // If RetailBusiness industry status is inactive, distribute based on /it-portal/topcoin-distribution-value
  if (
    distributionRule &&
    (distributionRule.mode === "topcoin_distribution_value" ||
      (distributionRule.is_industry_active === false && Number(distributionRule.price) > 0))
  ) {
    const price = Number(distributionRule.price) || 5;
    const topcoinRate = Number(distributionRule.topcoin_rate) || 1;
    const miniRate = Number(distributionRule.minitopcoin_rate) || 500;

    const units = Math.floor(amount / price);
    const topcoin = units * topcoinRate;
    const remainder = amount % price;
    const mTopcoin = Math.floor((remainder / price) * miniRate);

    return { topcoin, mTopcoin };
  }

  // Otherwise, distribute based on already defined system rule (£5 = 1 TopCoin)
  const ruleAmount = Number(distributionRule?.rule_amount) || TOPCOIN_RULE_AMOUNT;
  const topcoin = Math.floor(amount / ruleAmount);
  const remainder = amount % ruleAmount;
  const mTopcoin = Math.floor((remainder / ruleAmount) * 50);

  return { topcoin, mTopcoin };
};

function ManualTopCoinWindow() {
  const outletContext = useOutletContext();
  const userData = outletContext?.userData;
  const token = localStorage.getItem("auth_token");
  const [resolvedRetailBusinessId, setResolvedRetailBusinessId] = useState(null);
  const [isLoadingRetailBusiness, setIsLoadingRetailBusiness] = useState(false);
  const [distributionRule, setDistributionRule] = useState(userData?.distribution_rule || null);

  const [formData, setFormData] = useState({
    customer_no: "",
    transaction_total: "",
    invoice_no: "",
    transaction_datetime: getDefaultDateTime(),
    product_promotion_code: "",
    manual_override_reason: "",
    transaction_type: "Retail Purchase",
  });
  const [manualTopcoin, setManualTopcoin] = useState("");
  const [manualMTopcoin, setManualMTopcoin] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [isCustomerOptionsLoading, setIsCustomerOptionsLoading] = useState(false);
  const [customerSearchMessage, setCustomerSearchMessage] = useState("");
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const [customerValidation, setCustomerValidation] = useState({
    status: "idle",
    message: "",
    validatedValue: "",
  });
  const [collecardSelection, setCollecardSelection] = useState("");
  const customerValidationRequestRef = useRef(0);
  const customerSearchRequestRef = useRef(0);
  const customerSearchDebounceRef = useRef(null);
  const customerFieldWrapperRef = useRef(null);

  // Sync distribution rule from userData whenever it changes
  useEffect(() => {
    if (userData?.distribution_rule) {
      setDistributionRule(userData.distribution_rule);
    }
  }, [userData?.distribution_rule]);

  // Fetch the latest distribution rule on mount to ensure fresh state
  useEffect(() => {
    const fetchDistributionRule = async () => {
      try {
        const token = localStorage.getItem("auth_token");
        if (!token) return;
        const res = await fetch(`${API_URL}/api/retail-business/distribution-rule`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });
        if (res.ok) {
          const resData = await res.json();
          if (resData?.data) {
            setDistributionRule(resData.data);
          }
        }
      } catch (err) {
        console.error("Error fetching distribution rule:", err);
      }
    };

    fetchDistributionRule();
  }, []);

  // Pre-fetch retail business ID when component mounts
  useEffect(() => {
    const fetchRetailBusinessId = async () => {
      // First check if userData has the ID
      if (userData?.retail_business_id || userData?.id) {
        const id = userData.retail_business_id || userData.id;
        console.log("Using userData ID:", id);
        if (userData?.distribution_rule) {
          setDistributionRule(userData.distribution_rule);
        }
        setResolvedRetailBusinessId(id);
        return;
      }

      if (!token) {
        console.error("No auth token found");
        toast.error("Authentication token missing. Please log in again.");
        return;
      }

      if (resolvedRetailBusinessId !== null) return;

      setIsLoadingRetailBusiness(true);
      try {
        console.log("Fetching retail business info from API...");
        const res = await fetch(`${API_URL}/api/retail-business/me`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });

        console.log("API Response status:", res.status);

        if (!res.ok) {
          const errorText = await res.text();
          console.error("API Error:", errorText);
          throw new Error(`API returned ${res.status}: ${errorText}`);
        }

        const data = await res.json();
        console.log("API Response data:", data);

        // Try multiple possible paths for the user ID
        const id = data?.user?.retail_business_id || 
                   data?.user?.id || 
                   data?.retail_business_id || 
                   data?.user?.user_id ||
                   data?.id;

        if (!id) {
          console.error("No ID found in response:", data);
          throw new Error("Could not find retail business ID in response");
        }

        console.log("Resolved retail business ID:", id);
        setResolvedRetailBusinessId(id);
        if (data?.user?.distribution_rule) {
          setDistributionRule(data.user.distribution_rule);
        } else {
          fetch(`${API_URL}/api/retail-business/distribution-rule`, {
            headers: { Authorization: `Bearer ${token}` }
          })
            .then((r) => r.json())
            .then((d) => { if (d?.data) setDistributionRule(d.data); })
            .catch(() => {});
        }
      } catch (error) {
        console.error("Failed to fetch retail business:", error);
        toast.error("Unable to verify retail business account. Please contact support.");
      } finally {
        setIsLoadingRetailBusiness(false);
      }
    };

    fetchRetailBusinessId();
  }, [userData, token]);

  const calculatedReward = useMemo(
    () => calculateReward(formData.transaction_total, distributionRule),
    [formData.transaction_total, distributionRule]
  );

  const overrideTopcoin =
    manualTopcoin === "" ? calculatedReward.topcoin : Number(manualTopcoin);
  const overrideMTopcoin =
    manualMTopcoin === "" ? calculatedReward.mTopcoin : Number(manualMTopcoin);
  const hasManualOverride =
    overrideTopcoin !== calculatedReward.topcoin ||
    overrideMTopcoin !== calculatedReward.mTopcoin;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        customerFieldWrapperRef.current &&
        !customerFieldWrapperRef.current.contains(event.target)
      ) {
        setIsCustomerDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (customerSearchDebounceRef.current) {
        clearTimeout(customerSearchDebounceRef.current);
      }
    };
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    const nextValue = value;
    setFormData((prev) => ({ ...prev, [name]: nextValue }));

    if (name === "customer_no") {
      const trimmedValue = nextValue.trim();
      setCustomerValidation((prev) => {
        if (prev.validatedValue === trimmedValue && prev.status === "valid") {
          return prev;
        }

        return {
          status: trimmedValue ? "idle" : "invalid",
          message: trimmedValue ? "" : "Customer ID is required.",
          validatedValue: "",
        };
      });

      setIsCustomerDropdownOpen(true);
      setCollecardSelection("");
    }
  };

  const searchCustomers = async (keyword) => {
    if (!token) return;

    const requestId = customerSearchRequestRef.current + 1;
    customerSearchRequestRef.current = requestId;
    setIsCustomerOptionsLoading(true);
    setCustomerSearchMessage("");

    try {
      const query = encodeURIComponent(keyword.trim());
      const response = await fetch(
        `${API_URL}/api/retail-business/customers/search?q=${query}&limit=20`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      const data = await response.json();

      if (requestId !== customerSearchRequestRef.current) {
        return;
      }

      if (!response.ok || !data.success) {
        setCustomerOptions([]);
        setCustomerSearchMessage("Unable to load customers.");
        return;
      }

      const options = Array.isArray(data.data) ? data.data : [];
      setCustomerOptions(options);
      if (options.length === 0) {
        setCustomerSearchMessage("No matching customers found.");
      }
    } catch (error) {
      if (requestId !== customerSearchRequestRef.current) {
        return;
      }
      setCustomerOptions([]);
      setCustomerSearchMessage("Unable to load customers.");
    } finally {
      if (requestId === customerSearchRequestRef.current) {
        setIsCustomerOptionsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!isCustomerDropdownOpen || !token) return;

    if (customerSearchDebounceRef.current) {
      clearTimeout(customerSearchDebounceRef.current);
    }

    customerSearchDebounceRef.current = setTimeout(() => {
      searchCustomers(formData.customer_no);
    }, 300);
  }, [formData.customer_no, isCustomerDropdownOpen, token]);

  const handleSelectCustomerOption = (customer) => {
    const selectedCustomerId = String(customer.id);
    setFormData((prev) => ({ ...prev, customer_no: selectedCustomerId }));
    setIsCustomerDropdownOpen(false);
    setCustomerValidation({
      status: "idle",
      message: "",
      validatedValue: "",
    });
    setCollecardSelection("");
    validateCustomerNo(selectedCustomerId);
  };

  const validateCustomerNo = async (customerNo) => {
    const normalizedCustomerNo = customerNo.trim();

    if (!normalizedCustomerNo) {
      setCustomerValidation({
        status: "invalid",
        message: "Customer ID is required.",
        validatedValue: "",
      });
      return false;
    }

    if (!/^\d+$/.test(normalizedCustomerNo)) {
      setCustomerValidation({
        status: "invalid",
        message: "Customer ID must contain digits only.",
        validatedValue: "",
      });
      return false;
    }

    if (!token) {
      setCustomerValidation({
        status: "invalid",
        message: "Authentication token missing. Please log in again.",
        validatedValue: "",
      });
      return false;
    }

    const requestId = customerValidationRequestRef.current + 1;
    customerValidationRequestRef.current = requestId;
    setCustomerValidation({
      status: "checking",
      message: "Validating customer...",
      validatedValue: normalizedCustomerNo,
    });

    try {
      const response = await fetch(`${API_URL}/api/retail-business/customer/validate`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ customer_no: normalizedCustomerNo }),
      });

      const data = await response.json();

      // Ignore stale responses if user changed input and fired another request.
      if (requestId !== customerValidationRequestRef.current) {
        return false;
      }

      if (!response.ok || !data.success || !data?.data?.is_valid) {
        setCustomerValidation({
          status: "invalid",
          message: data?.message || "Customer is not eligible for this transaction.",
          validatedValue: normalizedCustomerNo,
        });
        return false;
      }

      setCustomerValidation({
        status: "valid",
        message: "Customer ID validated successfully.",
        validatedValue: normalizedCustomerNo,
      });
      setCollecardSelection(
        data.data?.has_active_collectable_card ? "yes" : "no"
      );
      return true;
    } catch (error) {
      setCustomerValidation({
        status: "invalid",
        message: "Unable to validate customer right now. Please try again.",
        validatedValue: normalizedCustomerNo,
      });
      return false;
    }
  };

  const handleCustomerNoBlur = () => {
    const normalizedCustomerNo = formData.customer_no.trim();

    // Delay blur so option click can complete.
    setTimeout(() => {
      setIsCustomerDropdownOpen(false);
    }, 150);

    if (!normalizedCustomerNo) {
      setCustomerValidation({
        status: "invalid",
        message: "Customer ID is required.",
        validatedValue: "",
      });
      return;
    }

    if (
      customerValidation.status === "valid" &&
      customerValidation.validatedValue === normalizedCustomerNo
    ) {
      return;
    }

    validateCustomerNo(normalizedCustomerNo);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Debug logging
    console.log("Submit triggered. State:", {
      resolvedRetailBusinessId,
      isLoadingRetailBusiness,
      hasToken: !!token,
      userData: userData,
      formData: formData
    });

    // Wait for retail business ID to be resolved if still loading
    if (isLoadingRetailBusiness) {
      toast.info("Please wait, verifying retail business account...");
      return;
    }

    // Get retail business ID
    let retailBusinessId = resolvedRetailBusinessId;
    
    // If still not resolved, try to get it from userData directly
    if (!retailBusinessId && userData) {
      retailBusinessId = userData.retail_business_id || userData.id;
      console.log("Using userData fallback:", retailBusinessId);
    }

    // If still no ID, try one more API call
    if (!retailBusinessId && token) {
      toast.info("Verifying account details...");
      try {
        const res = await fetch(`${API_URL}/api/retail-business/me`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });

        if (res.ok) {
          const data = await res.json();
          retailBusinessId = data?.user?.retail_business_id || 
                            data?.user?.id || 
                            data?.retail_business_id;
          if (retailBusinessId) {
            setResolvedRetailBusinessId(retailBusinessId);
          }
        }
      } catch (error) {
        console.error("Fallback API call failed:", error);
      }
    }

    if (!retailBusinessId) {
      console.error("No retail business ID available");
      toast.error("Unable to verify retail business account. Please ensure you are logged in as a retail business user.");
      return;
    }

    const normalizedCustomerNo = formData.customer_no.trim();
    if (!normalizedCustomerNo) {
      setCustomerValidation({
        status: "invalid",
        message: "Customer ID is required.",
        validatedValue: "",
      });
      return;
    }

    if (!/^\d+$/.test(normalizedCustomerNo)) {
      setCustomerValidation({
        status: "invalid",
        message: "Customer ID must contain digits only.",
        validatedValue: "",
      });
      return;
    }

    const needsValidation =
      customerValidation.status !== "valid" ||
      customerValidation.validatedValue !== normalizedCustomerNo;

    if (needsValidation) {
      const isCustomerValid = await validateCustomerNo(normalizedCustomerNo);
      if (!isCustomerValid) {
        toast.error("Please use a valid Customer ID for an approved, email-verified customer.");
        return;
      }
    }

    if (hasManualOverride && !formData.manual_override_reason.trim()) {
      toast.error("Please add a reason for manual override.");
      return;
    }

    if (!collecardSelection) {
      toast.error("Please select Collectable Card (ColleCard) Yes or No.");
      return;
    }

    setIsSubmitting(true);
    setResponseData(null);

    try {
      const payload = {
        retail_business_id: Number(retailBusinessId),
        customer_no: Number(normalizedCustomerNo),
        transaction_total: Number(formData.transaction_total),
        invoice_no: formData.invoice_no.trim(),
        transaction_datetime: new Date(formData.transaction_datetime).toISOString(),
        product_promotion_code: formData.product_promotion_code.trim() || null,
        has_collectable_card: collecardSelection === "yes",
        transaction_type: formData.transaction_type,
      };

      if (hasManualOverride) {
        payload.manual_awarding_topcoin = overrideTopcoin;
        payload.manual_awarding_mtopcoin = overrideMTopcoin;
        payload.manual_override_reason = formData.manual_override_reason.trim();
      }

      console.log("Submitting payload:", payload);

      const response = await fetch(`${API_URL}/api/retail-business/transaction`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      console.log("Response data:", data);

      if (!response.ok || !data.success) {
        const firstError = data?.errors
          ? Object.values(data.errors)[0]?.[0]
          : data?.message;
        throw new Error(firstError || "Transaction submission failed.");
      }

      setResponseData(data.data);
      toast.success("Transaction completed successfully.");
      
      // Reset form? Optional
      // setFormData({
      //   ...formData,
      //   transaction_total: "",
      //   invoice_no: "",
      // });
      
    } catch (error) {
      console.error("Transaction error:", error);
      toast.error(error.message || "Failed to complete transaction.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format current date & time for header display
  const now = new Date();
  const displayDate = now.toLocaleDateString("en-GB");
  const displayTime = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

  // Compute remainder value for display (£ value of mTopcoin portion)
  const transactionAmount = Number(formData.transaction_total) || 0;
  const remainderValue = (transactionAmount % TOPCOIN_RULE_AMOUNT).toFixed(2);

  const cardPromotion = responseData?.distribution_results?.card_promotion || null;
  const collectableCardCheckStatus = (() => {
    if (responseData && cardPromotion) {
      return cardPromotion.has_collectable_card ? "yes" : "no";
    }
    if (collecardSelection) {
      return collecardSelection;
    }
    return "pending";
  })();
  const expectedExtraPromotion = overrideTopcoin;
  const expectedBonusPromotion = overrideTopcoin * 16;

  const checklistItems = [
    {
      label: "Customer validated (approved + email verified)",
      status: customerValidation.status === "valid" ? "done" : "pending",
      detail:
        customerValidation.status === "valid"
          ? "Validated"
          : "Required before processing",
    },
    {
      label: "Collectable card active (ColleCard = Yes)",
      status:
        collectableCardCheckStatus === "yes"
          ? "done"
          : collectableCardCheckStatus === "no"
            ? "skipped"
            : "pending",
      detail:
        collectableCardCheckStatus === "yes"
          ? responseData
            ? "Yes (applied)"
            : "Yes (selected)"
          : collectableCardCheckStatus === "no"
            ? responseData
              ? "No (promotion not applied)"
              : "No (selected)"
            : "Select Yes or No",
    },
    {
      label: "Extra Card Promotion credited (row 13)",
      status:
        collectableCardCheckStatus === "yes" &&
        typeof cardPromotion?.extra_card_promotion === "number"
          ? "done"
          : collectableCardCheckStatus === "no"
            ? "skipped"
            : "pending",
      detail:
        typeof cardPromotion?.extra_card_promotion === "number"
          ? `+${cardPromotion.extra_card_promotion} TC`
          : collectableCardCheckStatus === "no"
            ? "Skipped"
            : collectableCardCheckStatus === "yes"
              ? `Will credit +${expectedExtraPromotion} TC`
              : "Pending",
    },
    {
      label: "Bonus Card Promotion credited (row 14)",
      status:
        collectableCardCheckStatus === "yes" &&
        typeof cardPromotion?.bonus_card_promotion === "number"
          ? "done"
          : collectableCardCheckStatus === "no"
            ? "skipped"
            : "pending",
      detail:
        typeof cardPromotion?.bonus_card_promotion === "number"
          ? `+${cardPromotion.bonus_card_promotion} TC`
          : collectableCardCheckStatus === "no"
            ? "Skipped"
            : collectableCardCheckStatus === "yes"
              ? `Will credit +${expectedBonusPromotion} TC`
              : "Pending",
    },
  ];

  if (isLoadingRetailBusiness) {
    return (
      <div className="mtcw-loading">
        <p>Loading retail business information...</p>
      </div>
    );
  }

  return (
    <div className="mtcw-outer">
      {/* Dark purple decorative border frame */}
      <div className="mtcw-border-frame">
        {/* White receipt card */}
        <div className="mtcw-card">

          {/* ---- Header ---- */}
          <div className="mtcw-header">
            <div className="mtcw-header-left">
              <h3>TopCoin UK<br />(TCUK)</h3>
            </div>
            <div className="mtcw-header-right">
              {displayDate}{" "}{displayTime}
            </div>
          </div>

          {/* ---- Branding row ---- */}
          <div className="mtcw-branding">
            <div className="mtcw-branding-center">
              <p className="brand-name">TopCoinLand</p>
              <a
                className="brand-link"
                href="https://www.topcoinland.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                www.topcoinland.com
              </a>
            </div>
            {/* <div className="mtcw-branding-right">
              {resolvedRetailBusinessId || "—"}
            </div> */}
          </div>

          {/* <hr className="mtcw-divider" /> */}

          {/* ---- Form ---- */}
          <form onSubmit={handleSubmit}>

            {/* Customer ID */}
            <div className="mtcw-form-row">
              <span className="label-text">
                Customer ID:
              </span>
              <div className="mtcw-customer-field-wrapper" ref={customerFieldWrapperRef}>
                <input
                  className="field-input mtcw-customer-field-input"
                  name="customer_no"
                  value={formData.customer_no}
                  onChange={handleChange}
                  onFocus={() => setIsCustomerDropdownOpen(true)}
                  onBlur={handleCustomerNoBlur}
                  placeholder="Type Customer ID or search by name"
                  required
                 maxLength={255} />
                {isCustomerDropdownOpen && (
                  <div className="mtcw-customer-dropdown">
                    {isCustomerOptionsLoading && (
                      <div className="mtcw-customer-dropdown-state">Searching customers...</div>
                    )}
                    {!isCustomerOptionsLoading && customerSearchMessage && (
                      <div className="mtcw-customer-dropdown-state">{customerSearchMessage}</div>
                    )}
                    {!isCustomerOptionsLoading &&
                      customerOptions.map((customer) => (
                        <button
                          type="button"
                          key={customer.id}
                          className="mtcw-customer-option"
                          onMouseDown={() => handleSelectCustomerOption(customer)}
                        >
                          <span className="mtcw-customer-option-main">
                            {customer.id} - {customer.full_name}
                          </span>
                          {/* <span
                            className={`mtcw-customer-option-status ${
                              customer.is_eligible ? "is-eligible" : "is-ineligible"
                            }`}
                          >
                            {customer.is_eligible
                              ? "Eligible"
                              : `Ineligible (${customer.status || "unknown"})`}
                          </span> */}
                        </button>
                      ))}
                  </div>
                )}
              </div>
              {/* {customerValidation.status !== "idle" && (
                <p
                  className={`mtcw-validation-message ${
                    customerValidation.status === "valid"
                      ? "is-valid"
                      : customerValidation.status === "checking"
                        ? "is-checking"
                        : "is-invalid"
                  }`}
                >
                  {customerValidation.message}
                </p>
              )} */}
            </div>

            <div className="mtcw-form-row">
              <span className="label-text">Collectable Card (ColleCard):</span>
              <select
                className="field-input mtcw-collecard-select"
                value={collecardSelection}
                onChange={(event) => setCollecardSelection(event.target.value)}
                disabled={customerValidation.status !== "valid"}
                required
              >
                <option value="">Select Yes or No</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>

            {/* Transaction Total */}
            <div className="mtcw-form-row">
              <span className="label-text">Transaction Total:</span>
              <input
                className="field-input currency-input"
                name="transaction_total"
                type="number"
                min="0"
                step="0.01"
                placeholder="£0.00"
                value={formData.transaction_total}
                onChange={handleChange}
                required
              />
            </div>

            {/* Awarding TopCoin */}
            <div className="mtcw-award-row">
              <span className="label-text">Awarding<br />TopCoin:</span>
              <input
                className="field-input"
                value={calculatedReward.topcoin}
                disabled
               maxLength={255} />
            </div>

            {/* Awarding M-TopCoin */}
            <div className="mtcw-award-row">
              <span className="label-text">Awarding M-TopCoin:</span>
              <input
                className="field-input"
                value={calculatedReward.mTopcoin}
                disabled
               maxLength={255} />
            </div>

            {/* Invoice No */}
            <div className="mtcw-form-row">
              <span className="label-text">Invoice No</span>
              <input
                className="field-input"
                name="invoice_no"
                value={formData.invoice_no}
                onChange={handleChange}
                required
               maxLength={255} />
            </div>

            {/* Transaction Type */}
            <div className="mtcw-form-row">
              <span className="label-text">Transaction Type:</span>
              <select
                className="field-input"
                name="transaction_type"
                value={formData.transaction_type}
                style={{width:"210px"}}
                onChange={handleChange}
                required
              >
                <option value="Retail Purchase">Retail Purchase</option>
                <option value="Collectable Card Purchase">Collectable Card Purchase</option>
                <option value="Collectable Card Transfer">Collectable Card Transfer</option>
                <option value="Refund">Refund</option>
                <option value="Auction">Auction</option>
              </select>
            </div>

            {/* Hidden datetime — still submitted but not shown in receipt layout */}
            <input
              type="hidden"
              name="transaction_datetime"
              value={formData.transaction_datetime}
             maxLength={255} />

            {/* Manual override section (appears only when values differ) */}
            {hasManualOverride && (
              <div className="mtcw-override-section">
                <label>
                  Manual Override Reason
                  <textarea
                    name="manual_override_reason"
                    value={formData.manual_override_reason}
                    onChange={handleChange}
                    placeholder="Please provide a reason for the manual override..."
                   maxLength={65535} />
                </label>
              </div>
            )}

            {/* Submit */}
            <div className="mtcw-submit-area">
              <button
                type="submit"
                className="mtcw-submit-btn"
                disabled={
                  isSubmitting ||
                  !resolvedRetailBusinessId ||
                  customerValidation.status === "checking" ||
                  customerValidation.status !== "valid" ||
                  !collecardSelection
                }
              >
                {isSubmitting ? "PROCESSING..." : "COMPLETE TRANSACTION"}
              </button>
            </div>
          </form>

          {/* <div className="mtcw-checklist">
            <h4 className="mtcw-checklist-title">Collectable Cards Checklist</h4>
            <ul className="mtcw-checklist-list">
              {checklistItems.map((item) => (
                <li
                  key={item.label}
                  className={`mtcw-checklist-item is-${item.status}`}
                >
                  <span className="mtcw-checklist-status">
                    {item.status === "done"
                      ? "✓"
                      : item.status === "skipped"
                        ? "—"
                        : "•"}
                  </span>
                  <span className="mtcw-checklist-label">{item.label}</span>
                  <span className="mtcw-checklist-detail">{item.detail}</span>
                </li>
              ))}
            </ul>
            <p className="mtcw-checklist-note">
              Extra and Bonus Card Promotion credits are applied only when the
              customer has an active collectable card. Values appear after a
              successful transaction.
            </p>
          </div> */}

          {/* ---- Response ---- */}
          {responseData && (
            <div className="mtcw-response">
              <p>
                <strong>SBC Ref:</strong> {responseData.sbc_ref}
              </p>
              <p>
                <strong>Awarded:</strong>{" "}
                {responseData.awarding_topcoin} TopCoin /{" "}
                {responseData.awarding_mtopcoin} M-TopCoin
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ManualTopCoinWindow;
